import { useCurrentUser } from '@/hooks/useCurrentUser';
import { alert } from '@/services/alert';
import { fetchBot } from '@/services/services';
import { Button, Container, Paper, Stack, Textarea, Title, Text, Alert } from '@mantine/core';
import { useMedplum } from '@medplum/react';
import { IconAlertCircle } from '@tabler/icons-react';
import { ReactElement, useState } from 'react';

type UpdateProcedureStatusResult = {
  procedureStage?: string;
  error?: string;
};

export function UpdatePatientStatesPage(): ReactElement {
  const medplum = useMedplum();
  const {
    currentUserPermissions: { isAdmin, hasAdminMembership },
  } = useCurrentUser();
  const [patientIds, setPatientIds] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    const trimmedIds = patientIds.trim();

    if (!trimmedIds) {
      alert.error('Enter at least one patient ID.');
      return;
    }

    // Parse the IDs - support comma-separated or newline-separated
    const idsArray = trimmedIds
      .split(/[,\n]/)
      .map((id) => id.trim())
      .filter(Boolean);

    if (idsArray.length === 0) {
      alert.error('No valid patient IDs found.');
      return;
    }

    try {
      setIsSubmitting(true);

      let succeeded = 0;
      let failed = 0;

      for (const patientId of idsArray) {
        try {
          const result = await fetchBot<UpdateProcedureStatusResult>(medplum, 'update-procedure-status', {
            patient_id: patientId,
            is_healthie_patient: false,
          });

          if (result?.error) {
            failed++;
          } else {
            succeeded++;
          }
        } catch {
          failed++;
        }
      }

      if (failed === 0) {
        alert.success(`Successfully updated procedure stage for ${succeeded} patient(s).`);
        setPatientIds('');
      } else {
        alert.warning(
          `Update completed with issues: ${succeeded} succeeded, ${failed} failed. Check console for details.`
        );
      }
    } catch (error) {
      alert.error(`Update failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container size="sm" py="xl">
      <Paper p="lg" shadow="sm">
        <Title order={2} mb="lg">
          Update Patient States
        </Title>

        {!isAdmin && !hasAdminMembership && (
          <Alert icon={<IconAlertCircle size={16} />} title="Access Denied" color="red" mb="md">
            This page is only accessible to administrators.
          </Alert>
        )}

        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Enter Medplum patient IDs to update their procedure stage. You can enter multiple IDs separated by commas or
            newlines.
          </Text>

          <Textarea
            label="Patient IDs"
            placeholder="patient-1, patient-2, patient-3&#10;or&#10;patient-1&#10;patient-2&#10;patient-3"
            value={patientIds}
            onChange={(event) => setPatientIds(event.currentTarget.value)}
            minRows={6}
            required
            description="Enter Medplum patient IDs separated by commas or newlines"
          />

          <Button
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={(!isAdmin && !hasAdminMembership) || !patientIds.trim()}
            fullWidth
          >
            Update Patient States
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
}
